import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { track } from "@/lib/track";

/**
 * <JuiceLayer/> — one non-blocking, skippable toast for reward/completion
 * moments. Listens to `mwq-juice` (lib/juice.js) and the legacy
 * `we-celebrate` (stamp) + `we-badge-earned` events so existing reward
 * writes get polished feedback without a second reward system.
 * Never covers the question/answer area: bottom-anchored, above bottom nav.
 */
const TIER_MS = { seedlings: 3600, explorers: 2800, pioneers: 2000 };
const KIND_LABEL = { complete: "Complete", badge: "Badge earned", stamp: "Passport stamp", xp: "Progress", levelup: "Level up", discovery: "Discovery", story: "Story", surprise: "Surprise" };
const KIND_TINT = { badge: "mwq-card-tint-reward", stamp: "mwq-card-tint-success", discovery: "mwq-card-tint-success", surprise: "mwq-card-tint-creative", story: "mwq-card-tint-creative", levelup: "mwq-card-tint-reward", complete: "mwq-card-tint-primary", xp: "" };

export default function JuiceLayer() {
  const [item, setItem] = useState(null);
  const queue = useRef([]);
  const timer = useRef(null);
  const showing = useRef(false);

  const showNext = () => {
    const next = queue.current.shift();
    showing.current = !!next;
    setItem(next || null);
    if (!next) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setItem(null); track("celebration_completed", { kind: next.kind, skipped: 0 }); setTimeout(showNext, 120); }, TIER_MS[next.tier] || 2800);
  };

  useEffect(() => {
    const push = (d) => { if (!d?.kind || d.kind === "correct" || d.kind === "wrong") return; queue.current.push(d); if (!showing.current) showNext(); };
    const onJuice = (e) => push(e.detail);
    const onLegacy = (e) => {
      const k = e?.detail?.kind;
      if (k === "stamp") import("@/lib/juice").then((m) => m.juice("stamp", { code: e.detail.code }));
    };
    const onBadge = (e) => import("@/lib/juice").then((m) => m.juice("badge", { label: e?.detail?.label }));
    window.addEventListener("mwq-juice", onJuice);
    window.addEventListener("we-celebrate", onLegacy);
    window.addEventListener("we-badge-earned", onBadge);
    return () => { window.removeEventListener("mwq-juice", onJuice); window.removeEventListener("we-celebrate", onLegacy); window.removeEventListener("we-badge-earned", onBadge); clearTimeout(timer.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!item) return null;
  const skip = () => { clearTimeout(timer.current); setItem(null); track("celebration_completed", { kind: item.kind, skipped: 1 }); setTimeout(showNext, 80); };
  const sp = item.line?.speaker;
  const seed = item.tier === "seedlings";
  const pio = item.tier === "pioneers";
  return (
    <div className="fixed inset-x-0 bottom-[calc(72px+env(safe-area-inset-bottom))] md:bottom-6 z-[75] flex justify-center px-3 pointer-events-none" data-testid="juice-layer" role="status" aria-live="polite">
      <div className={`mwq-toast mwq-card ${KIND_TINT[item.kind] || ""} pointer-events-auto flex items-center gap-3 max-w-md w-full ${seed ? "py-4" : "py-3"}`} data-testid={`juice-toast-${item.kind}`} onClick={skip}>
        {sp && !pio && (
          <span className={`inline-flex shrink-0 items-center justify-center rounded-full ${sp.color} ${seed ? "h-14 w-14 text-3xl" : "h-11 w-11 text-2xl"} ${item.kind === "stamp" ? "mwq-juice-stamp" : "mwq-juice-correct"}`} aria-label={sp.name}>{sp.emoji}</span>
        )}
        <div className="min-w-0 flex-1">
          <p className="mwq-meta" data-testid="juice-kind">{KIND_LABEL[item.kind] || "Progress"}{item.label ? ` · ${item.label}` : ""}</p>
          <p className={`font-extrabold text-[var(--color-text)] leading-snug ${seed ? "text-lg" : "text-base"}`} data-testid="juice-line">{item.text || item.line?.text}</p>
          {item.to ? <Link to={item.to} onClick={skip} className="mwq-btn mwq-btn-tertiary min-h-[36px] px-2 text-sm" data-testid="juice-link">Take a look</Link> : sp && <p className="mwq-meta mt-0.5" data-testid="juice-speaker">— {sp.name}</p>}
        </div>
        <button type="button" onClick={(e) => { e.stopPropagation(); skip(); }} aria-label="Dismiss" data-testid="juice-skip" className="shrink-0 h-11 w-11 inline-flex items-center justify-center rounded-full hover:bg-black/5"><X size={18} /></button>
      </div>
    </div>
  );
}
